/**
 * Brand-override layers for the current identity.
 *
 * The system DEFAULT brand for every protocol/standard lives in the
 * `@authe/protocol-ui` catalog. This hook supplies the overrides layered on top
 * (resolved by the package's `useBrand` with `user > tenant > default`
 * precedence):
 *  - USER layer — persisted in `ScopedStorage` (scoped to the session identity)
 *    via `BrandPrefsStore`, so a user's tweaks survive reloads and never leak
 *    across tenants.
 *  - TENANT layer — owned by the catalog field-overrides service. Wired here
 *    once that resource exposes brand overrides; until then it's empty and
 *    resolution falls through to the user pref + system default.
 *
 * No editor writes these yet, so today the user layer is typically empty and the
 * catalog default renders — but the resolution path is live, so adding an editor
 * is purely additive.
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSessionIdentityKey } from "@authe/ui-core/session";
import { usePermissions } from "@authe/ui-core/permissions";
import { brandsFromProtocolSummaries } from "@authe/protocol-ui";
import { useProtocolsSource } from "@/api/adapters/analytics";
import { createScopedStorage } from "@authe/fe-commons/storage";
import { useOptionalTenant } from "@authe/ui-core/tenant";
import { createBrandPrefsStore, type BrandOverrideLayers } from "@authe/protocol-ui";
import { APPLICATION_SLUG, RESOURCES } from "@/config";

/** One identity-keyed catalogue read, shared with the entity references view. */
export function useRegistryBrands() {
  const source = useProtocolsSource();
  const identity = useSessionIdentityKey();
  const { can } = usePermissions();
  const query = useQuery({
    queryKey: ["entity-protocol-catalogue", identity],
    enabled: Boolean(identity) && can(RESOURCES.metadataRegistry, "analytics"),
    queryFn: () => source.list(),
    staleTime: 60_000,
  });
  return React.useMemo(() => brandsFromProtocolSummaries(query.data ?? []), [query.data]);
}

export function useBrandOverrides(): BrandOverrideLayers {
  const tenant = useOptionalTenant();
  return React.useMemo<BrandOverrideLayers>(() => {
    const storage = createScopedStorage({
      app: APPLICATION_SLUG,
      customerId: tenant?.customerId ?? null,
      orgId: tenant?.orgId ?? null,
      tenantId: tenant?.tenantId ?? null,
      userId: tenant?.userId ?? null,
    });
    return { user: createBrandPrefsStore(storage).getOverrides() };
  }, [tenant?.customerId, tenant?.orgId, tenant?.tenantId, tenant?.userId]);
}
